import { guideVideo } from "./guide-video";

export type PageVideoLink = {
  provider: "YouTube" | "Vimeo" | "Twitch";
  /** The provider's own page for the video, for the link beside the player. */
  watchUrl: string;
  /** The player, loaded only once a visitor presses Play. */
  embedUrl: string;
  /** Twitch's player has to be told which site embeds it (pageVideoPlayerUrl). */
  needsParent: boolean;
};

const twitchHosts = new Set(["twitch.tv", "www.twitch.tv", "m.twitch.tv", "clips.twitch.tv"]);
const twitchVideoId = /^[0-9]{1,20}$/;
const twitchClip = /^[A-Za-z0-9_-]{1,100}$/;
const twitchChannel = /^[A-Za-z0-9_]{3,25}$/;
// Twitch's own pages, which share the channel address shape and are never channels.
const twitchPages = new Set([
  "directory",
  "downloads",
  "drops",
  "inventory",
  "jobs",
  "messages",
  "p",
  "prime",
  "search",
  "settings",
  "store",
  "subscriptions",
  "turbo",
  "videos",
  "wallet",
]);

/** Twitch's "1h2m3s" start time, kept only in that exact shape. */
function twitchTime(value: string | null) {
  return value && /^(?:\d{1,2}h)?(?:\d{1,2}m)?(?:\d{1,2}s)?$/.test(value) && value !== ""
    ? value
    : null;
}

/**
 * A Twitch past broadcast, clip or channel, rebuilt from its parts: nothing authored beyond the
 * video, clip or channel name reaches the player's address.
 * https://dev.twitch.tv/docs/embed/video-and-clips/
 */
function twitchVideo(source: string): PageVideoLink | null {
  let url: URL;
  try {
    url = new URL(source);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.username || url.password || url.port) return null;
  if (!twitchHosts.has(url.hostname)) return null;
  const parts = url.pathname.split("/").filter(Boolean);
  const player = new URL("https://player.twitch.tv/");
  if (url.hostname === "clips.twitch.tv") {
    const clip = parts.length === 1 ? parts[0]! : "";
    if (!twitchClip.test(clip)) return null;
    const embed = new URL("https://clips.twitch.tv/embed");
    embed.searchParams.set("clip", clip);
    return { provider: "Twitch", watchUrl: url.href, embedUrl: embed.href, needsParent: true };
  }
  if (parts[0] === "videos" && parts.length === 2 && twitchVideoId.test(parts[1]!)) {
    player.searchParams.set("video", `v${parts[1]}`);
    const time = twitchTime(url.searchParams.get("t"));
    if (time) player.searchParams.set("time", time);
  } else if (parts.length === 3 && parts[1] === "clip" && twitchClip.test(parts[2]!)) {
    const embed = new URL("https://clips.twitch.tv/embed");
    embed.searchParams.set("clip", parts[2]!);
    return { provider: "Twitch", watchUrl: url.href, embedUrl: embed.href, needsParent: true };
  } else if (
    parts.length === 1 &&
    twitchChannel.test(parts[0]!) &&
    !twitchPages.has(parts[0]!.toLowerCase())
  ) {
    player.searchParams.set("channel", parts[0]!.toLowerCase());
  } else return null;
  return { provider: "Twitch", watchUrl: url.href, embedUrl: player.href, needsParent: true };
}

/**
 * A channel's uploads as a playlist (`UU…`, or `UULF…` for its long-form videos), the newest
 * first. Only that playlist shape reaches the player, never an authored query.
 */
function youtubeUploads(source: string): PageVideoLink | null {
  let url: URL;
  try {
    url = new URL(source);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.username || url.password || url.port) return null;
  if (!["youtube.com", "www.youtube.com", "m.youtube.com"].includes(url.hostname)) return null;
  if (url.pathname.replace(/\/$/, "") !== "/playlist") return null;
  const lists = url.searchParams.getAll("list");
  if (lists.length !== 1 || !/^UU(?:LF)?[A-Za-z0-9_-]{22}$/.test(lists[0]!)) return null;
  const embed = new URL("https://www.youtube-nocookie.com/embed/videoseries");
  embed.searchParams.set("list", lists[0]!);
  embed.searchParams.set("autoplay", "1");
  embed.searchParams.set("playsinline", "1");
  const watch = new URL("https://www.youtube.com/playlist");
  watch.searchParams.set("list", lists[0]!);
  return { provider: "YouTube", watchUrl: watch.href, embedUrl: embed.href, needsParent: false };
}

/**
 * The video links a page can play: YouTube and Vimeo exactly as posts take them, a YouTube
 * channel's uploads, and Twitch past broadcasts, clips and channels. Anything else is refused, so a page never frames an
 * unknown site.
 */
export function pageVideoLink(value: string): PageVideoLink | null {
  // oxlint-disable-next-line eslint/no-control-regex -- Reject control characters in authored video URLs.
  if (value.length > 1000 || /[\u0000-\u001f\u007f\\]/.test(value)) return null;
  const source = value.trim();
  if (!/^https:\/\//i.test(source)) return null;
  const uploads = youtubeUploads(source);
  if (uploads) return uploads;
  const known = guideVideo(source);
  if (known)
    return known.embedUrl
      ? {
          provider: known.provider,
          watchUrl: known.watchUrl,
          embedUrl: known.embedUrl,
          needsParent: false,
        }
      : null;
  return twitchVideo(source);
}

/**
 * The player's address on the page showing it. Twitch plays only on sites it is told about.
 * A player pressed into place plays at once. One that loads with the page (`withPage`) can't
 * start with sound, so Twitch starts muted, and other providers wait on their own Play button.
 */
export function pageVideoPlayerUrl(video: PageVideoLink, host: string, withPage = false) {
  const url = new URL(video.embedUrl);
  if (video.needsParent) url.searchParams.set("parent", host);
  if (video.provider === "Twitch") {
    url.searchParams.set("autoplay", "true");
    if (withPage) url.searchParams.set("muted", "true");
  } else if (withPage) url.searchParams.delete("autoplay");
  return url.href;
}
